import { motion } from 'motion/react';
import { AlertCircle, Package, ShoppingBag } from 'lucide-react';
import { useLandingContent } from '../../lib/content';

interface Props {
  open: boolean;
  onClose: () => void;
  missingPackaging: boolean;
  missingMerch: boolean;
  /**
   * N6c — the box's first/second option-step TITLES, used to render the
   * "Missing X / Go to X" copy. The deep-link targets are already positional
   * (App.tsx passes the first two option-step ids); these titles finish the
   * job so the copy reads correctly for ANY box structure. Optional — when
   * absent the copy falls back to the legacy "Packaging"/"Merch" literals so
   * Spring/Office render byte-identically (their titles ARE Packaging/Merch).
   */
  packagingTitle?: string;
  merchTitle?: string;
  onGoToPackaging: () => void;
  onGoToMerch: () => void;
}

export function ConfigErrorModal({
  open,
  onClose,
  missingPackaging,
  missingMerch,
  packagingTitle,
  merchTitle,
  onGoToPackaging,
  onGoToMerch,
}: Props) {
  // Derive the slot labels from the box's own step titles, falling back to the
  // legacy literals. For Spring/Office (titles "Packaging" / "Merch") this is
  // byte-identical.
  const packagingLabel = packagingTitle?.trim() || 'Packaging';
  const merchLabel = merchTitle?.trim() || 'Merch';
  const { modals } = useLandingContent('modals');
  const m = modals.configError ?? {
    title: 'Incomplete Box',
    body:
      'To request a proposal, your box must meet the following requirements. Please make these changes:',
    cta: 'Close',
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="relative bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl text-center"
        role="alertdialog"
        aria-labelledby="modal-title"
        aria-describedby="modal-desc"
      >
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h3 id="modal-title" className="text-2xl font-bold mb-4 text-[#444444]">
          {m.title}
        </h3>
        <div id="modal-desc" className="text-gray-600 mb-8 space-y-4">
          <p className="text-sm">{m.body}</p>
          <ul className="text-sm font-medium space-y-3 text-left">
            {missingPackaging && (
              <li className="flex items-start gap-3 p-3 bg-red-50 rounded-xl border border-red-100 text-red-700">
                <Package className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-xs">
                    Missing {packagingLabel}
                  </p>
                  <p className="text-xs opacity-90">
                    Please select a box type or packaging style.
                  </p>
                </div>
              </li>
            )}
            {missingMerch && (
              <li className="flex items-start gap-3 p-3 bg-red-50 rounded-xl border border-red-100 text-red-700">
                <ShoppingBag className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-xs">
                    No {merchLabel} added
                  </p>
                  <p className="text-xs opacity-90">
                    Please add at least one item to your box.
                  </p>
                </div>
              </li>
            )}
          </ul>
        </div>
        <div className="flex flex-col gap-3">
          {missingPackaging && (
            <button
              onClick={onGoToPackaging}
              className="w-full py-4 bg-[#356d4e] text-white rounded-xl font-bold hover:brightness-110 transition-all shadow-lg"
            >
              Go to {packagingLabel}
            </button>
          )}
          {missingMerch && (
            <button
              onClick={onGoToMerch}
              className="w-full py-4 bg-[#356d4e] text-white rounded-xl font-bold hover:brightness-110 transition-all shadow-lg"
            >
              Go to {merchLabel}
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full py-4 bg-gray-100 text-gray-600 rounded-xl font-bold hover:bg-gray-200 transition-colors"
          >
            {m.cta ?? 'Close'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
